import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as api from '../api/worldGraph'
import type {
  RoundAction,
  RoundEdits,
  TeamLinks,
  WorldGraphEdits,
} from '../api/worldGraph'
import type { BuildStep, ReviewStep, WorldGraphState } from '../types'

const POLL_MS = 2000

const FINISHED: Record<BuildStep, string> = {
  sidecar: 'The baseline context is ready to review.',
  stocks: 'The stocks are ready to review.',
  links: 'The causal links are ready to review.',
  finalize: 'The world graph is ready.',
  proposals: "The teams' changes are ready to review.",
  artifacts: 'The news and briefs are ready to review.',
}

const ROUND_FAILURES: Record<RoundAction, string> = {
  propose: 'Could not start proposing the changes.',
  apply: 'Could not apply the round.',
  undo: 'Could not undo the round.',
  write: 'Could not start writing the news.',
  release: 'Could not release the news.',
}

export const STEP_NAMES: Record<BuildStep, string> = {
  sidecar: 'baseline context',
  stocks: 'stocks',
  links: 'causal links',
  finalize: 'drift rules',
  proposals: 'changes from decisions',
  artifacts: 'news and briefs',
}

export function errorMessage(err: unknown, fallback: string): string {
  const data = (
    err as {
      response?: { data?: { detail?: string; problems?: string[] } }
    }
  )?.response?.data
  if (!data?.detail) return fallback
  const problems = data.problems ?? []
  if (!problems.length) return data.detail
  const shown = problems.slice(0, 3).join(' ')
  const more = problems.length > 3 ? ` (and ${problems.length - 3} more)` : ''
  return `${data.detail} ${shown}${more}`
}

/**
 * The room's world graph, polled while a build step runs in the background.
 */
export function useWorldGraph(roomId: string | undefined) {
  const [state, setState] = useState<WorldGraphState | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<{
    status?: number
    message: string
  } | null>(null)
  const lastStatus = useRef<string | undefined>(undefined)

  const refresh = useCallback(async () => {
    if (!roomId) return
    try {
      const { data } = await api.getWorldGraph(roomId)
      setState(data)
      setLoadError(null)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setLoadError({
        status,
        message: errorMessage(err, 'Could not load the world graph.'),
      })
    } finally {
      setLoading(false)
    }
  }, [roomId])

  useEffect(() => {
    setLoading(true)
    refresh()
  }, [refresh])

  const build = state?.current_build
  const running = build?.status === 'running'

  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(refresh, POLL_MS)
    return () => window.clearInterval(timer)
  }, [running, refresh])

  // Tell the professor when a run they were waiting on ends.
  useEffect(() => {
    const previous = lastStatus.current
    lastStatus.current = build?.status
    if (previous !== 'running' || !build || build.status === 'running') return
    if (build.status === 'succeeded') {
      toast.success(FINISHED[build.step])
    } else {
      toast.error(build.error || 'The build step did not finish.')
    }
  }, [build])

  const act = useCallback(
    async (
      call: () => Promise<{ data: WorldGraphState }>,
      failure: string
    ): Promise<boolean> => {
      try {
        const { data } = await call()
        setState(data)
        return true
      } catch (err) {
        toast.error(errorMessage(err, failure))
        // The server may have moved on (another tab, a finished run).
        refresh()
        return false
      }
    },
    [refresh]
  )

  return {
    state,
    loading,
    loadError,
    running,
    refresh,
    setEnabled: (enabled: boolean) =>
      act(
        () => api.updateWorldGraph(roomId!, { enabled }),
        'Could not change the world graph setting.'
      ),
    save: (edits: WorldGraphEdits) =>
      act(
        () => api.updateWorldGraph(roomId!, edits),
        'Could not save your changes.'
      ),
    generate: (step: BuildStep) =>
      act(
        () => api.generateStep(roomId!, step),
        `Could not start the ${STEP_NAMES[step]}.`
      ),
    approve: (step: ReviewStep, edits: WorldGraphEdits = {}) =>
      act(
        () => api.approveStep(roomId!, step, edits),
        `Could not approve the ${STEP_NAMES[step]}.`
      ),
    setUpTeams: (links: TeamLinks) =>
      act(() => api.setUpTeams(roomId!, links), 'Could not set up the teams.'),
    roundAction: (round: number, action: RoundAction, ids?: string[]) =>
      act(
        () => api.roundAction(roomId!, round, action, ids ? { ids } : {}),
        ROUND_FAILURES[action]
      ),
    editRound: (round: number, edits: RoundEdits) =>
      act(
        () => api.editRound(roomId!, round, edits),
        'Could not save your changes.'
      ),
    resetWorld: () =>
      act(() => api.resetWorld(roomId!), 'Could not reset the world.'),
    reopen: (step: ReviewStep) =>
      act(
        () => api.reopenStep(roomId!, step),
        `Could not re-open the ${STEP_NAMES[step]}.`
      ),
  }
}

export type WorldGraphController = ReturnType<typeof useWorldGraph>
